import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card, { StatCard } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { useToast } from '../context/ToastContext';

const SSMCompanies = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [ssmCompanies, setSsmCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null); // company
  const [deleteTarget, setDeleteTarget] = useState(null); // company
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Obține TOATE firmele
      const response = await api.get('/companies');
      const allComps = response.data;

      setAllCompanies(allComps);

      // Filtrează firmele SSM
      const ssm = allComps.filter((c) => c.type === 'SSM');
      setSsmCompanies(ssm);
    } catch (err) {
      console.error('Fetch error:', err);
      showToast(t('ssmCompanies.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getClientCompanies = (ssmCompanyId) => {
    return allCompanies.filter((c) => c.type === 'CLIENT' && c.parentCompanyId === ssmCompanyId);
  };

  const filteredCompanies = ssmCompanies.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cui.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = (companyId) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };

  const openDeactivate = (company) => {
    setDeactivateTarget(company);
    setActionError('');
  };
  const openDelete = (company) => {
    setDeleteTarget(company);
    setDeleteConfirmName('');
    setActionError('');
  };

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    setActionError('');
    try {
      await api.patch(`/companies/${deactivateTarget.id}/deactivate`);
      setDeactivateTarget(null);
      await fetchData();
    } catch (err) {
      setActionError(err.response?.data?.error || t('ssmCompanies.deactivateError'));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmName !== deleteTarget.name) {
      setActionError(t('ssmCompanies.nameMismatch'));
      return;
    }
    setActionLoading(true);
    setActionError('');
    try {
      await api.delete(`/companies/${deleteTarget.id}`);
      setDeleteTarget(null);
      setDeleteConfirmName('');
      await fetchData();
    } catch (err) {
      setActionError(
        err.response?.data?.error || err.response?.data?.details || t('ssmCompanies.deleteError')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const totalClients = allCompanies.filter((c) => c.type === 'CLIENT').length;
  const totalEmployees = ssmCompanies.reduce((sum, c) => sum + (c._count?.employees || 0), 0);

  if (loading) {
    return <Spinner size="lg" label={t('ssmCompanies.loadingCompanies')} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('companies.ssmCompaniesTitle')}
        subtitle={t('ssmCompanies.subtitle')}
        actions={
          <Link to="/companies/new">
            <Button variant="primary" size="md">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {t('ssmCompanies.newSSMCompany')}
            </Button>
          </Link>
        }
      />

      {/* Search */}
      <Card>
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={t('companies.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10"
          />
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label={t('companies.ssmCompaniesTitle')}
          value={ssmCompanies.length}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
        />
        <StatCard
          label={t('companies.clientCompaniesTitle')}
          value={totalClients}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
              />
            </svg>
          }
        />
        <StatCard
          label={t('ssmCompanies.employeesSSM')}
          value={totalEmployees}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          }
        />
      </div>

      {/* Companies List */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('companies.ssmCompaniesTitle')} ({filteredCompanies.length})
        </h2>

        {filteredCompanies.length === 0 ? (
          <EmptyState
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            }
            title={searchTerm ? t('companies.noMatches') : t('companies.noSSM')}
            description={
              searchTerm ? t('ssmCompanies.tryOtherTerms') : t('ssmCompanies.createFirstSSM')
            }
            actionLabel={!searchTerm ? t('ssmCompanies.createSSMCompany') : undefined}
            onAction={!searchTerm ? () => navigate('/companies/new') : undefined}
          />
        ) : (
          <div className="space-y-4">
            {filteredCompanies.map((company) => {
              const clients = getClientCompanies(company.id);
              const isExpanded = expandedCompany === company.id;

              return (
                <div
                  key={company.id}
                  className="border-2 border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden hover:border-primary-400 transition-all"
                >
                  {/* Header firmă SSM */}
                  <div className="p-6 bg-gradient-to-r from-primary-50 to-surface dark:from-slate-950 dark:to-slate-900">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                            {company.name}
                          </h3>
                          <Badge color="blue">SSM</Badge>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                          <svg
                            className="w-4 h-4 text-slate-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0"
                            />
                          </svg>
                          {t('companies.cui')}: {company.cui}
                        </p>
                        {company.address && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                            <svg
                              className="w-4 h-4 text-slate-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            {company.address}
                          </p>
                        )}
                        {company.phone && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                            <svg
                              className="w-4 h-4 text-slate-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                            {company.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 dark:text-slate-400 mb-4 pb-4 border-b border-slate-200 dark:border-slate-800">
                      <span className="flex items-center gap-1">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                          {company._count?.employees || 0}
                        </span>{' '}
                        {t('ssmCompanies.employeesSSMLabel')}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold text-primary-600 dark:text-primary-300">
                          {clients.length}
                        </span>{' '}
                        {t('ssmCompanies.clientCompaniesLabel')}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                          {company._count?.assignments || 0}
                        </span>{' '}
                        {t('ssmCompanies.assignmentsLabel')}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-400 dark:text-slate-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {new Date(company.createdAt).toLocaleDateString('ro-RO')}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <Link to={`/companies/${company.id}`} className="flex-1">
                        <Button variant="primary" size="md" className="w-full justify-center">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                          {t('ssmCompanies.viewDetails')}
                        </Button>
                      </Link>

                      <button
                        onClick={() => toggleExpand(company.id)}
                        className={`px-4 py-2.5 rounded-lg font-medium transition text-sm flex items-center gap-1.5 ${
                          clients.length > 0
                            ? 'bg-accent-50 dark:bg-accent-950/50 text-accent-700 dark:text-accent-300 hover:bg-accent-100'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-400 cursor-not-allowed'
                        }`}
                        disabled={clients.length === 0}
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                        {isExpanded ? t('ssmCompanies.hide') : t('ssmCompanies.show')}{' '}
                        {t('ssmCompanies.clientsLabel')} ({clients.length})
                      </button>

                      {company.active !== false && (
                        <Button
                          variant="ghost"
                          size="md"
                          onClick={() => openDeactivate(company)}
                          className="text-warning-700 dark:text-warning-300"
                        >
                          {t('companies.deactivate')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="md"
                        onClick={() => openDelete(company)}
                        className="text-danger-700 dark:text-danger-300"
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>

                  {/* Lista clienți (expandabilă) */}
                  {isExpanded && clients.length > 0 && (
                    <div className="bg-surface-secondary border-t-2 border-slate-200 dark:border-slate-800 p-6">
                      <h4 className="font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-slate-500 dark:text-slate-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                          />
                        </svg>
                        <span>
                          {t('companies.clientCompaniesTitle')} ({clients.length})
                        </span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {clients.map((client) => (
                          <div
                            key={client.id}
                            className="bg-surface rounded-lg p-4 border-2 border-slate-200 dark:border-slate-800 hover:border-accent-400 transition-all"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-bold text-slate-900 dark:text-slate-100">
                                    {client.name}
                                  </h5>
                                  <Badge color="green">CLIENT</Badge>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {t('companies.cui')}: {client.cui}
                                </p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400 mb-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                              <span className="flex items-center gap-1">
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                {client._count?.employees || 0} {t('ssmCompanies.employeesLabel')}
                              </span>
                              <span className="flex items-center gap-1">
                                <svg
                                  className="w-3.5 h-3.5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                  />
                                </svg>
                                {client._count?.assignments || 0}{' '}
                                {t('ssmCompanies.assignmentsLabel')}
                              </span>
                            </div>

                            <Link to={`/companies/${client.id}`} className="block w-full">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-center text-accent-700 dark:text-accent-300 hover:bg-accent-50"
                              >
                                {t('ssmCompanies.viewDetails')}
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </Button>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Modal: Dezactivare */}
      <Modal
        open={!!deactivateTarget}
        onClose={() => {
          setDeactivateTarget(null);
          setActionError('');
        }}
        title={t('ssmCompanies.deactivateTitle')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeactivateTarget(null)}
              disabled={actionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="warning" onClick={confirmDeactivate} loading={actionLoading}>
              {t('ssmCompanies.confirmDeactivate')}
            </Button>
          </div>
        }
      >
        {deactivateTarget &&
          (() => {
            const clientsCount = getClientCompanies(deactivateTarget.id).length;
            return (
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p>
                  <Trans
                    i18nKey="ssmCompanies.deactivateConfirmQuestion"
                    values={{ name: deactivateTarget.name }}
                    components={{ b: <strong /> }}
                  />
                </p>
                {clientsCount > 0 && (
                  <p className="text-warning-700 dark:text-warning-300">
                    <Trans
                      i18nKey="ssmCompanies.deactivateClientsWarning"
                      values={{ count: clientsCount }}
                      components={{ b: <strong /> }}
                    />
                  </p>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('ssmCompanies.deactivateDataKept')}
                </p>
                {actionError && (
                  <p className="text-xs text-danger-600 dark:text-danger-300">{actionError}</p>
                )}
              </div>
            );
          })()}
      </Modal>

      {/* Modal: Ștergere definitivă */}
      <Modal
        open={!!deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteConfirmName('');
          setActionError('');
        }}
        title={t('ssmCompanies.deleteTitle')}
        footer={
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={actionLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              loading={actionLoading}
              disabled={actionLoading || deleteConfirmName !== deleteTarget?.name}
            >
              {t('companies.deleteForever')}
            </Button>
          </div>
        }
      >
        {deleteTarget &&
          (() => {
            const clientsCount = getClientCompanies(deleteTarget.id).length;
            return (
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <div className="bg-danger-50 dark:bg-danger-950/50 border border-danger-200 dark:border-danger-700 rounded-lg p-3">
                  <p className="font-semibold text-danger-800 dark:text-danger-300 mb-1">
                    {t('companies.deleteWarning')}
                  </p>
                  <p className="text-xs text-danger-700 dark:text-danger-300">
                    {clientsCount > 0 ? (
                      <Trans
                        i18nKey="ssmCompanies.deleteBodyWithClients"
                        values={{ name: deleteTarget.name, count: clientsCount }}
                        components={{ b: <strong /> }}
                      />
                    ) : (
                      <Trans
                        i18nKey="ssmCompanies.deleteBodyBase"
                        values={{ name: deleteTarget.name }}
                        components={{ b: <strong /> }}
                      />
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                    <Trans
                      i18nKey="ssmCompanies.confirmNamePrompt"
                      values={{ name: deleteTarget.name }}
                      components={{
                        code: <code className="font-mono text-danger-700 dark:text-danger-300" />,
                      }}
                    />
                  </p>
                  <Input
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder={deleteTarget.name}
                  />
                </div>
                {actionError && (
                  <p className="text-xs text-danger-600 dark:text-danger-300">{actionError}</p>
                )}
              </div>
            );
          })()}
      </Modal>
    </div>
  );
};

export default SSMCompanies;
